/**
 * How loud a voice is, from its samples, as the orb reads it.
 *
 * The orb's first ear read `expo-audio`'s meter, and on the S23 that meter did
 * not report usefully: the orb sat still while Devendra talked to it ("it does
 * not react to my voice"). The live call has the samples themselves — PCM16
 * frames of a tenth of a second, from the microphone and from the model — so
 * the loudness is computed here, from the bytes, the same way on every phone.
 *
 * Three steps, each pure so `bun test` proves it without a microphone:
 *
 *   1. `blockLevels` cuts a frame into 25 ms blocks and gives each its RMS in
 *      dBFS. One number a frame is ten lumps a second, and a syllable is
 *      shorter than a lump; four a frame is forty a second, which is what lets
 *      the orb follow speech syllable by syllable.
 *   2. `createRoomFloor` learns how loud the room is when nobody is speaking.
 *   3. `levelOf` places a block between that floor and full voice, 0..1.
 *
 * docs/plans/done/orb-live-call.md, decision 4.
 */

/** The quietest level a block can report: digital silence, and anything under it. */
export const MIN_DB = -100;

/** How long one block is. Four to a microphone frame. */
export const BLOCK_MS = 25;

/**
 * How far above the room a voice has to be before the orb moves at all, and
 * the span from there to full swell. The old ear's numbers, which were right;
 * its meter was what failed.
 */
export const ORB_FLOOR_DB = 4;
export const ORB_SPAN_DB = 28;

/**
 * The floor the model's own voice is measured against. The model's audio has
 * no room in it — it is synthesised, normalised speech, whose 25 ms blocks
 * run from about -45 to -15 dBFS — so a fixed floor under that range gives
 * the orb the whole of its span while the voice talks.
 */
export const SPEAKER_FLOOR_DB = -50;

/** One block of a frame: its loudness, and how long it lasts. */
export type LevelBlock = {
  readonly db: number;
  readonly ms: number;
};

/** The largest magnitude a signed 16-bit sample can hold, as the float scale uses it. */
const SCALE = 32768;

/**
 * A PCM16 little-endian mono frame at `rate`, as the loudness of each of its
 * blocks. A short tail — under half a block — joins the block before it
 * rather than standing alone, because a 3 ms block is one spike, not a
 * syllable. A trailing odd byte is dropped, as `pcm16ToFloat` drops it.
 */
export const blockLevels = (pcm: Uint8Array, rate: number, blockMs: number = BLOCK_MS): LevelBlock[] => {
  const samples = Math.floor(pcm.length / 2);
  if (samples === 0 || rate <= 0) return [];
  const size = Math.max(1, Math.round((rate * blockMs) / 1000));
  const edges: number[] = [];
  for (let edge = size; edge < samples; edge += size) edges.push(edge);
  const tail = samples - (edges[edges.length - 1] ?? 0);
  if (edges.length > 0 && tail < size / 2) edges.pop();
  edges.push(samples);

  const blocks: LevelBlock[] = [];
  let from = 0;
  for (const to of edges) {
    let sum = 0;
    for (let at = from; at < to; at += 1) {
      const low = pcm[at * 2] ?? 0;
      const high = pcm[at * 2 + 1] ?? 0;
      const raw = ((high << 8) | low) & 0xffff;
      const value = (raw >= 0x8000 ? raw - 0x10000 : raw) / SCALE;
      sum += value * value;
    }
    const count = to - from;
    const rms = Math.sqrt(sum / count);
    blocks.push({ db: rms > 0 ? Math.max(MIN_DB, 20 * Math.log10(rms)) : MIN_DB, ms: (count / rate) * 1000 });
    from = to;
  }
  return blocks;
};

/** A block's loudness placed between the floor and full voice, 0..1. */
export const levelOf = (db: number, floorDb: number): number =>
  Math.max(0, Math.min(1, (db - floorDb - ORB_FLOOR_DB) / ORB_SPAN_DB));

export type RoomFloorOptions = {
  /** How many of the latest blocks the floor is read from: two seconds of 25 ms blocks. */
  readonly window: number;
  /** Which share of those blocks is at or under the floor. */
  readonly percentile: number;
  /** The floor never goes under this: digital silence is not a room. */
  readonly lowestDb: number;
  /** Nor over this: a room this loud is a voice, and the orb must still move for the next. */
  readonly highestDb: number;
};

export const ROOM_DEFAULTS: RoomFloorOptions = {
  window: 80,
  percentile: 0.2,
  lowestDb: -70,
  highestDb: -30,
};

export type RoomFloor = {
  /** One block's loudness; answers the floor with it counted. */
  readonly feed: (db: number) => number;
  /** A new call: the room is learnt again. */
  readonly reset: () => void;
};

/**
 * How loud the room is, learnt from the blocks as they come.
 *
 * The old ear took the loudest of the first readings. That is wrong for the
 * orb in particular: the person tapped it to say something, so the first
 * 400 ms are often their voice, the floor was set at it, and the orb stayed
 * still for the whole turn. Here the floor is a low percentile of the last two
 * seconds. Speech is never solid — there are gaps between words, and within a
 * word between syllables — so a fifth of any two seconds is the room, even
 * while someone talks over it; and a room that gets louder (a fan, a road)
 * raises the floor within those two seconds, so its noise stops moving the orb.
 *
 * The clamps are for the two ends a microphone reaches that are not a room:
 * zeros (the simulator's first frames, a muted input) would put the floor at
 * -100 and let any whisper fill the orb, and a floor taken from a shout would
 * hold it still.
 */
export const createRoomFloor = (options: Partial<RoomFloorOptions> = {}): RoomFloor => {
  const settings: RoomFloorOptions = { ...ROOM_DEFAULTS, ...options };
  let recent: number[] = [];

  const feed = (db: number): number => {
    recent.push(Number.isFinite(db) ? db : MIN_DB);
    if (recent.length > settings.window) recent = recent.slice(recent.length - settings.window);
    const sorted = [...recent].sort((a, b) => a - b);
    const pick = sorted[Math.min(sorted.length - 1, Math.floor(settings.percentile * sorted.length))] ?? MIN_DB;
    return Math.max(settings.lowestDb, Math.min(settings.highestDb, pick));
  };

  const reset = (): void => {
    recent = [];
  };

  return { feed, reset };
};

/**
 * A lead the player puts before the first frame of a turn: `LEAD_S` in
 * `live-audio.ts`, in milliseconds.
 */
export const PLAYBACK_LEAD_MS = 20;

/**
 * When a frame of the model's voice will be heard, on the JS clock, and where
 * the next one goes after it. The player schedules on its own clock by the
 * same rule — the later of "now plus a lead" and where the last frame ended —
 * so the orb can move with the frame it is hearing rather than the frame that
 * just arrived, which is up to a second ahead of the speaker in a burst.
 * `playhead` 0 is an empty pipe: after a cut-in, or before the first frame.
 */
export const playbackSlot = (
  nowMs: number,
  playheadMs: number,
  frameMs: number,
  leadMs: number = PLAYBACK_LEAD_MS,
): { readonly atMs: number; readonly playheadMs: number } => {
  const atMs = Math.max(nowMs + leadMs, playheadMs);
  return { atMs, playheadMs: atMs + frameMs };
};
